import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getInviteApi, setInvitePasswordApi } from '../lib/api';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function SetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [inviteData, setInviteData] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await getInviteApi(token);
        setInviteData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid invite link');
      } finally {
        setLoading(false);
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) { toast.error('Enter a password'); return; }
    if (password.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }

    setSubmitting(true);
    try {
      const res = await setInvitePasswordApi(token, password);
      localStorage.setItem('pp_token', res.data.token);
      toast.success('Password set! Welcome aboard.');
      // Small delay so toast shows before redirect
      setTimeout(() => { window.location.href = '/operator'; }, 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set password');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="invite-error-page">
        <div className="max-w-sm w-full text-center">
          <div className="w-14 h-14 rounded-2xl bg-status-held-bg flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-status-held" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Invite Not Valid</h2>
          <p className="text-sm text-gray-500 mb-6">{error}</p>
          <Button
            onClick={() => navigate('/login')}
            className="bg-gray-900 hover:bg-gray-800"
            data-testid="go-to-login-btn"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="set-password-page">
      <div className="max-w-sm w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-brand-teal-tint flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-brand-teal" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Set Your Password</h2>
          <p className="text-sm text-gray-500 mt-1">
            Welcome, <span className="font-medium text-gray-700">{inviteData?.name}</span>
          </p>
          <p className="text-xs text-gray-400 mt-0.5">{inviteData?.email}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="password" className="text-sm text-gray-600 mb-1.5">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Choose a password"
              className="rounded-xl bg-gray-50 border-transparent focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20"
              data-testid="set-password-input"
            />
          </div>
          <div>
            <Label htmlFor="confirm" className="text-sm text-gray-600 mb-1.5">Confirm Password</Label>
            <Input
              id="confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
              className="rounded-xl bg-gray-50 border-transparent focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20"
              data-testid="confirm-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded-full py-5 font-semibold shadow-lg transition-all active:scale-[0.98]"
            data-testid="set-password-submit-btn"
          >
            {submitting ? 'Setting password...' : 'Set Password & Continue'}
          </Button>
        </form>
      </div>
    </div>
  );
}
